"use client";

import {
  AcademicCapIcon,
  BriefcaseIcon,
  VideoCameraIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  UserIcon,
  CheckIcon,
  ClockIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useState, useEffect, startTransition } from "react";
import { Features, FeatureItem } from "./features";
import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import Link from "next/link";

export function UnifiedLanding() {
  const tHero = useTranslations("Hero");
  const t = useTranslations("UnifiedLanding");
  const typingPhrases = tHero.raw("typingPhrases") as string[];

  const solutions = [
    {
      name: t("solutions.coaches.name"),
      description: t("solutions.coaches.description"),
      icon: UserGroupIcon,
      href: "/solutions/coaches",
      color: "bg-blue-600",
      features: t.raw("solutions.coaches.features") as string[],
    },
    {
      name: t("solutions.professionals.name"),
      description: t("solutions.professionals.description"),
      icon: BriefcaseIcon,
      href: "/solutions/professionals",
      color: "bg-purple-600",
      features: t.raw("solutions.professionals.features") as string[],
    },
    {
      name: t("solutions.creators.name"),
      description: t("solutions.creators.description"),
      icon: VideoCameraIcon,
      href: "/solutions/creators",
      color: "bg-pink-600",
      features: t.raw("solutions.creators.features") as string[],
    },
    {
      name: t("solutions.educators.name"),
      description: t("solutions.educators.description"),
      icon: AcademicCapIcon,
      href: "/solutions/educators",
      color: "bg-indigo-600",
      features: t.raw("solutions.educators.features") as string[],
    },
  ];

  const keyFeatures = [
    {
      name: t("features.publicProfile.name"),
      description: t("features.publicProfile.description"),
      icon: UserIcon,
    },
    {
      name: t("features.smartScheduling.name"),
      description: t("features.smartScheduling.description"),
      icon: CalendarDaysIcon,
    },
    {
      name: t("features.flexiblePricing.name"),
      description: t("features.flexiblePricing.description"),
      icon: CurrencyDollarIcon,
    },
    {
      name: t("features.instantPayments.name"),
      description: t("features.instantPayments.description"),
      icon: CreditCardIcon,
    },
    {
      name: t("features.globalReach.name"),
      description: t("features.globalReach.description"),
      icon: GlobeAltIcon,
    },
    {
      name: t("features.secureReliable.name"),
      description: t("features.secureReliable.description"),
      icon: ShieldCheckIcon,
    },
  ];

  const comparisonFeatures = t.raw("comparisonFeatures") as Array<{
    feature: string;
    livemonetize: string;
    competitors: string;
  }>;
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    const currentPhrase = typingPhrases[currentPhraseIndex];

    if (isTyping) {
      if (displayedText.length < currentPhrase.length) {
        const timeout = setTimeout(
          () => {
            setDisplayedText(currentPhrase.slice(0, displayedText.length + 1));
          },
          80 + Math.random() * 40,
        );
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setIsTyping(false);
        }, 2500);
        return () => clearTimeout(timeout);
      }
    } else if (displayedText.length > 0) {
      const timeout = setTimeout(() => {
        setDisplayedText(displayedText.slice(0, -1));
      }, 30);
      return () => clearTimeout(timeout);
    } else {
      startTransition(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % typingPhrases.length);
        setIsTyping(true);
      });
    }
  }, [displayedText, isTyping, currentPhraseIndex, typingPhrases]);

  return (
    <>
      {/* Hero Section */}
      <div className="bg-white dark:bg-slate-900">
        <div className="relative isolate px-6 pt-10 lg:px-8">
          <div className="mx-auto max-w-4xl py-16 sm:py-32">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="text-5xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:text-7xl dark:text-white">
                  <span className="block">{tHero("titlePrefix")}</span>
                  <span className="block min-h-[1.1em] text-purple-600 dark:text-purple-400">
                    {displayedText}
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{
                        duration: 0.7,
                        repeat: Infinity,
                        repeatType: "reverse",
                      }}
                      className="text-purple-600"
                    >
                      |
                    </motion.span>
                  </span>
                </h1>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mx-auto mt-8 max-w-2xl text-lg leading-8 text-gray-600 sm:text-xl dark:text-gray-300"
              >
                {tHero("description")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mt-8 inline-flex items-center gap-x-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
              >
                <ClockIcon className="h-4 w-4" />
                {tHero("badge")}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="mt-10 flex items-center justify-center gap-x-6"
              >
                {/* Used prefetch={false} to avoid caching the link for sign-in */}
                <Link
                  href="/creator"
                  prefetch={false}
                  className="rounded-lg bg-purple-600 px-8 py-4 text-base font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
                >
                  {tHero("ctaPrimary")}
                </Link>

                <Link
                  href="#demo"
                  className="text-base leading-6 font-semibold text-gray-900 transition-colors hover:text-purple-600 dark:text-white dark:hover:text-purple-400"
                >
                  {tHero("ctaSecondary")} <span aria-hidden="true">→</span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Solutions Section */}
      <div className="bg-gray-50 dark:bg-slate-800 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base leading-7 font-semibold text-purple-600 dark:text-purple-400">
              {t("solutionsEyebrow")}
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
              {t("solutionsTitle")}
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              {t("solutionsDescription")}
            </p>
          </div>

          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:grid-cols-2 lg:max-w-none lg:grid-cols-4">
            {solutions.map((solution) => (
              <Link
                key={solution.name}
                href={solution.href}
                className="group relative bg-white rounded-2xl p-6 shadow-sm ring-1 ring-gray-900/5 transition-all hover:shadow-lg hover:ring-purple-500 dark:bg-slate-900 dark:ring-white/10 dark:hover:ring-purple-400"
              >
                <div
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${solution.color} mb-4`}
                >
                  <solution.icon
                    className="h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                  {solution.name}
                </h3>
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                  {solution.description}
                </p>
                <ul className="mt-4 space-y-2">
                  {solution.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start text-sm text-gray-500 dark:text-gray-400"
                    >
                      <CheckIcon className="mr-2 h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <Features
        eyebrow={t("featuresEyebrow")}
        title={t("featuresTitle")}
        description={t("featuresDescription")}
      >
        {keyFeatures.map((feature) => (
          <FeatureItem
            key={feature.name}
            name={feature.name}
            description={feature.description}
          >
            <feature.icon aria-hidden="true" className="h-6 w-6 text-white" />
          </FeatureItem>
        ))}
      </Features>

      {/* Comparison Section */}
      <div className="bg-linear-to-b from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-900 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base leading-7 font-semibold text-purple-600 dark:text-purple-400">
              {t("comparisonEyebrow")}
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
              {t("comparisonTitle")}
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              {t("comparisonDescription")}
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-5xl">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div className="bg-white rounded-2xl p-8 shadow-lg ring-1 ring-purple-600/10 dark:bg-slate-800 dark:ring-white/10">
                <div className="text-center">
                  <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
                    <CurrencyDollarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                    LiveMonetize
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    {t("comparisonLiveMonetizeDescription")}
                  </p>
                  <div className="mt-6 flex items-center justify-center gap-x-2">
                    <CheckIcon className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {t("comparisonFreeToStart")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/10">
                <div className="text-center">
                  <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                    <CalendarDaysIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                    {t("comparisonKajabi")}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    {t("comparisonKajabiDescription")}
                  </p>
                  <div className="mt-6 flex items-center justify-center gap-x-2">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      $1,428+/year
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/10">
                <div className="text-center">
                  <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                    <VideoCameraIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                    {t("comparisonOtherPlatforms")}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    {t("comparisonOtherPlatformsDescription")}
                  </p>
                  <div className="mt-6 flex items-center justify-center gap-x-2">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t("comparisonComplexPricing")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-16 rounded-2xl bg-white shadow-xl ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/10">
              <div className="p-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  {t("comparisonDetailTitle")}
                </h3>
                {/* Desktop Table View */}
                <div className="hidden sm:block divide-y divide-gray-200 dark:divide-white/10">
                  {/* Header Row */}
                  <div className="py-4 grid grid-cols-3 gap-4 border-b-2 border-gray-300 dark:border-white/20 pb-4 mb-2">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {t("comparisonFeatureLabel")}
                    </div>
                    <div className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                      LiveMonetize
                    </div>
                    <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {t("comparisonCompetitorsLabel")}
                    </div>
                  </div>
                  {comparisonFeatures.map((item, idx) => (
                    <div key={idx} className="py-4 grid grid-cols-3 gap-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.feature}
                      </div>
                      <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                        {item.livemonetize}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {item.competitors}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Mobile Card View */}
                <div className="sm:hidden space-y-4">
                  {comparisonFeatures.map((item, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-gray-200 dark:border-white/10 p-4 space-y-3"
                    >
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {item.feature}
                      </div>
                      <div className="space-y-2">
                        <div>
                          <div className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
                            LiveMonetize
                          </div>
                          <div className="text-sm text-purple-600 dark:text-purple-400">
                            {item.livemonetize}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                            {t("comparisonCompetitorsLabel")}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">
                            {item.competitors}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-purple-600 dark:bg-purple-700">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t("ctaTitle")}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-purple-100">
              {t("ctaDescription")}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              {/* Used prefetch={false} to avoid caching the link for sign-in */}
              <Link
                href="/creator"
                prefetch={false}
                className="rounded-lg bg-white px-8 py-4 text-base font-semibold text-purple-600 shadow-sm transition-all hover:bg-purple-50 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("ctaPrimary")}
              </Link>
              <Link
                href="#demo"
                className="text-base leading-6 font-semibold text-white hover:text-purple-100 transition-colors"
              >
                {t("ctaSecondary")} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
